import * as React from "react";
import { useTranslation } from "react-i18next";
import { useWindowDimensions, StyleSheet } from "react-native";
import { Paragraph, useTheme, Title } from "react-native-paper";
import { useSelector } from "react-redux";
import { TYPE_CHOOSE_BENE } from "../../../../modules/beneficiaries/utils/bene.utils";
import { ACCOUNT } from "../../../services/services-model.type";
import { spacing } from "@styles/spacing";
import { formatCurrency } from "../../../util/format-currency";
import { useMobileMediaQuery } from "@media-query";
import { Block } from "@block";
import { FlatListItem } from "./select-account-bene.modal";
import ItemBeneAccountModal from "./item/item-content";
import { RecyclerListView, DataProvider, LayoutProvider } from "recyclerlistview";
import { Constant } from "../../../constant/constant";
import { mapAccountPayments } from "app/shared/util/common";
import { getListAccountSelector } from "app/modules/account-management/reducer/selectors";

interface AccountSourceProps {
  title?: string;
  account?: ACCOUNT;
  typeArray?: Array<string>;
  checkCurrentCode?: boolean;
  onPressAcceptAccount?: Function;
}

const ViewTypes = {
  HEADER: 0,
  LISTITEM: 1,
};

let arrayHeader: Array<number> = [];

const AccountSourceModal = (props: AccountSourceProps) => {
  const { title, typeArray = [], onPressAcceptAccount = () => {}, checkCurrentCode = false } = props;
  const { t } = useTranslation();
  const { colors, fonts } = useTheme();
  const { width, height } = useWindowDimensions();
  const isMobile = useMobileMediaQuery();

  const listAccountStore = useSelector(getListAccountSelector);

  const [dataProvider] = React.useState<DataProvider>(
    new DataProvider((r1, r2) => {
      return r1 !== r2;
    }),
  );

  const [layoutProvider] = React.useState<LayoutProvider>(
    new LayoutProvider(
      (index) => {
        if (arrayHeader.length && arrayHeader.includes(index)) {
          return ViewTypes.HEADER;
        }
        return ViewTypes.LISTITEM;
      },
      (type, dim) => {
        switch (type) {
          case ViewTypes.HEADER:
            dim.width = isMobile ? width * 0.9 : 550 - spacing.huge * 2;
            dim.height = spacing.macro;
            break;
          case ViewTypes.LISTITEM:
            dim.width = isMobile ? width * 0.9 : 550 - spacing.huge * 2;
            dim.height = isMobile ? spacing.galactic + 30 : spacing.galactic;
            break;
          default:
            dim.width = 0;
            dim.height = 0;
            break;
        }
      },
    ),
  );

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        flatList: {
          flex: 1,
          paddingHorizontal: spacing.huge,
        },
        titleModal: {
          color: colors.accent,
          fontFamily: fonts.medium.fontFamily,
        },
        modalContainerStyle: {
          alignSelf: "center",
          height: height * 0.8,
          borderRadius: 10,
        },
      }),
    [colors.accent, fonts.medium.fontFamily, height],
  );

  const accountPayment = (e: ACCOUNT) => e.ACCOUNTTYPE === Constant.ACCOUNTTYPE.ACCOUNT_PAYMENT;
  const accountDeposit = (e: ACCOUNT) => Constant.ACCOUNTTYPE.ACCOUNT_TERM_DEPOSIT.includes(e.ACCOUNTTYPE || "");
  const accountLoan = (e: ACCOUNT) => e.ACCOUNTTYPE === Constant.ACCOUNTTYPE.ACCOUNT_LOAN;

  const onPressChoose = (item: FlatListItem) => {
    onPressAcceptAccount(item.data);
  };

  const getListAccount = () => {
    const list: ACCOUNT[] = listAccountStore?.ACCOUNT || [];
    if (list.length) {
      const filter = list.filter((item) => {
        if (typeArray.length && checkCurrentCode) {
          return item.ACCOUNTTYPE?.length && typeArray.includes(item.ACCOUNTTYPE) && item.CURRENCYCODE === "VND";
        } else if (typeArray.length) {
          return item.ACCOUNTTYPE?.length && typeArray.includes(item.ACCOUNTTYPE || "");
        } else if (checkCurrentCode) {
          return item.ACCOUNTTYPE?.length && item.CURRENCYCODE === "VND";
        } else {
          return item.ACCOUNTTYPE?.length;
        }
      });
      return mapAccountPayments(filter);
    }
    return list;
  };

  const getDataListAccounts = () => {
    let list: FlatListItem[] = [];
    const listAccount = getListAccount();
    if (listAccount.length) {
      const accountPayments: FlatListItem[] = [];
      const accountDeposits: FlatListItem[] = [];
      const accountLoans: FlatListItem[] = [];
      listAccount.forEach((acc: ACCOUNT) => {
        const obj: FlatListItem = {
          data: acc,
          type: TYPE_CHOOSE_BENE.ACCOUNT,
          label: acc.ACCOUNTNO || "",
          value: formatCurrency(acc.AVAILABLEBALANCE, acc.CURRENCYCODE),
        };
        if (accountPayment(acc)) {
          accountPayments.push(obj);
        } else if (accountDeposit(acc)) {
          obj.value = formatCurrency(acc.CURRENTBALANCE, acc.CURRENCYCODE);
          accountDeposits.push(obj);
        } else if (accountLoan(acc)) {
          obj.value = formatCurrency(acc.CURRENTBALANCE, acc.CURRENCYCODE);
          accountLoans.push(obj);
        }
      });
      if (accountPayments.length) {
        list.push({ header: { left: t("ACC_SO_TAI_KHOAN"), right: t("ACC_SO_DU_KHA_DUNG") } });
        list = list.concat(accountPayments);
        arrayHeader = [0];
      }
      if (accountDeposits.length) {
        arrayHeader.push(list.length);
        list = list.concat([{ header: { left: t("ACC_SO_TAI_KHOAN"), right: t("ACC_SO_DU_SO") } }]);
        list = list.concat(accountDeposits);
      }
      if (accountLoans.length) {
        arrayHeader.push(list.length);
        list = list.concat([{ header: { left: t("ACC_SO_TAI_KHOAN"), right: t("ACC_MAN_DU_NO_HIEN_TAI") } }]);
        list = list.concat(accountLoans);
      }
    }
    return list;
  };

  const getDataProvider = () => {
    arrayHeader = [0];
    const listAccounts = getDataListAccounts();
    return dataProvider.cloneWithRows(listAccounts);
  };

  const TitleModal = (
    <React.Fragment>
      <Block ph={spacing.huge} pv={spacing.medium}>
        <Title style={styles.titleModal}>{title || t("ACC_TAI_KHOAN_THANH_TOAN")}</Title>
      </Block>
    </React.Fragment>
  );

  const FormEmpty = () => {
    if (!arrayHeader.length)
      return (
        <Block pv={spacing.massive} center middle>
          <Paragraph style={[fonts.regular, { color: colors.text }]}>{t("COMMON_KHONG_CO_DU_LIEU")}</Paragraph>
        </Block>
      );
    return null;
  };

  const renderItem = (type: string | number, item: any, index: number) => {
    return <ItemBeneAccountModal item={item} index={index} onClick={onPressChoose} />;
  };

  return (
    <>
      {TitleModal}
      <RecyclerListView
        style={styles.flatList}
        dataProvider={getDataProvider()}
        layoutProvider={layoutProvider}
        onScroll={({ nativeEvent: { contentSize, layoutMeasurement, contentOffset } }) => {
          const distanceFromEnd = (contentSize?.height || 0) - (layoutMeasurement?.height || 0) - contentOffset.y;
          if (distanceFromEnd < 0) return false;
        }}
        renderAheadOffset={0}
        rowRenderer={renderItem}
        renderFooter={() => FormEmpty()}
      />
    </>
  );
};

export default AccountSourceModal;
